import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';

import { BookingAddOnVendorDto, BookingAddOnPaymentDto } from '@hqo/resource-booking-client';

import { ADD_ON_PAYMENT_STATUS, AddOnCharge, chargeFrom, paymentIdentity } from 'store/api-client/add-on-payment-types';
import { ResourceBookingServiceClient } from 'store/api-client/resource-booking-service-client';
import { RootState } from 'store/reducer';

/** How often to re-ask while the linked request and its session are still being made. */
const PENDING_POLL_MS = 2_000;

/** Long enough for the three hops, short enough that nobody stares at a skeleton. */
const PENDING_GIVE_UP_MS = 45_000;

/*
 * ⛔ A SECOND, SHORTER WAIT, and it is a different question from the one above. That one asks "will a
 * session ever exist"; this one asks "has the charge the tenant just made landed on the work order".
 * Measured on SR-DM4XHP5: the session was opened at 04:53:35 and `paymentState` flipped to `paid` at
 * 04:53:46. Eleven seconds is the shape of it, so fifteen is the cap.
 *
 * ⚠️ RUNNING OUT MEANS DIFFERENT THINGS TO THE TWO SIGNALS THAT ARM IT. After a REPORTED charge it is
 * not terminal: `refetchOnWindowFocus` below keeps asking, because billing said money moved. After a
 * mere dismissal it IS terminal — that signal gets this one window and nothing after it. See both
 * parameters for why the weaker one is not allowed to ask forever.
 *
 * ⚠️ A POLL IS THE WRONG SHAPE FOR THIS AND IT IS NOT YET AVOIDABLE. The obvious fix is to be told
 * instead of asking, and this repo already holds a proven SSE reader for exactly that
 * (`use-receipt-activity-stream.hook`, on search-service's per-user activity-events stream). A
 * payment does not reach it. Traced through both services:
 *
 *   - work-orders DOES record the settlement, richly — `payment-activity-events.service` writes a
 *     `PAYMENT_COMPLETED` row carrying gateway, totalCents, currency and cardLastFour — but it writes
 *     it to the WORK ORDER's own activity log, not to the Kafka topic search-service consumes.
 *   - search-service's `work-orders-events-consumer` handles two message types only,
 *     `WORK_ORDER_CREATED` and `WORK_ORDER_STATUS_UPDATED`, and the latter is emitted only when
 *     `status_id` changes.
 *   - A payment changes `payment_state`, never `status_id` — confirmed on live data, where a paid
 *     work order still read `status: NEW`.
 *
 * So the stream never fires for a payment. Closing that properly means work-orders emitting a
 * payment event and search-service indexing it: two other services, not this one.
 */
const SETTLE_GIVE_UP_MS = 15_000;

export interface AddOnPaymentQueryResult {
  /** The session billing opened against the linked request, once one exists. */
  checkoutSessionUuid: string | null;
  /** A session is still expected, so the step holds rather than declaring the order finished. */
  isAwaitingSession: boolean;
  /**
   * RES-1336 — what was actually CHARGED, once the work order says it was. Null until then, which is
   * what lets the confirmation tell "you paid $50.00 with ···· 4242" apart from "we estimate $50.00".
   */
  charge: AddOnCharge | null;
  /*
   * RES-1317 — the payment block itself, so the confirmation can ask `addOnReceipt` what to show
   * rather than treating the presence of a charge as the final answer. After an amend a charge
   * exists, has been released, and describes the order as it was.
   */
  payment: BookingAddOnPaymentDto | null;
}

const NOTHING_TO_PAY: AddOnPaymentQueryResult = {
  checkoutSessionUuid: null,
  isAwaitingSession: false,
  charge: null,
  payment: null,
};
const STILL_COMING: AddOnPaymentQueryResult = {
  checkoutSessionUuid: null,
  isAwaitingSession: true,
  charge: null,
  payment: null,
};

/*
 * RES-1336 — resolve the charge for a booking's add-ons, in ONE hop.
 *
 * resource-booking-service's add-ons read (RES-1316, in `@hqo/resource-booking-client` from
 * 1.1.55) answers one entry per vendor, each with a `status` and — once the linked request exists
 * and billing has priced it — a `payment` block carrying the checkout session and what was charged.
 *
 * ⭐ IT USED TO TAKE TWO, and the browser made both: this read for a `workOrderUuid`, then
 * work-orders' own tenant read for the session. That hop now happens server-side, inside the
 * service that already holds the link rows, which is why `getTenantWorkOrder` is gone from this app
 * entirely. The payment state is the same system of record either way; only who asks has changed.
 *
 * ⚠️ `payment` IS NULL FOR MORE THAN ONE REASON — no work order yet, or work-orders unreachable —
 * and the service draws that distinction deliberately rather than reporting "nothing to pay". Both
 * mean "keep waiting" here, which is what the deadline below is for.
 *
 * ⛔ ONLY ASK ONCE THERE IS AN ORDER TO ASK ABOUT. `isExpected` is the caller stating that it has
 * just placed a PAYABLE order, and it gates the whole query. Asking earlier is what made this
 * useless: the step mounts after the booking exists, so an ungated query ran on the picker screen,
 * found no link rows, cached "nothing to pay", and — because the poll only continues while a session
 * is awaited — never asked again. The submit handler then read that stale answer and skipped payment
 * entirely, no matter how correctly the vendor was configured.
 *
 * ⛔ AN EMPTY LIST MEANS "NOT YET", NOT "NEVER", and only because of the gate above. The link row is
 * written by a CDC consumer reacting to the add-on rows, so for the first moments after an order
 * there is nothing to find. Treating that as terminal reintroduces the same race one step later.
 *
 * ⚠️ EVERY FAILURE ENDS THE WAIT — BUT ONLY WHILE THERE IS STILL A WAIT TO END. A 403 (the caller
 * lacks `CAN_MANAGE_TENANT_WORK_ORDERS`), a `FAILED` link, a read that errors, or the deadline all
 * resolve to "nothing to pay" WHEN NO SESSION HAS BEEN FOUND. The add-ons are already ordered by
 * then — the vendor can still bill — so the cost of giving up is a charge collected elsewhere, while
 * the cost of waiting forever is a tenant on a screen with no exit.
 *
 * ⛔ ONCE A SESSION IS KNOWN, NOTHING MAY TAKE IT BACK. Fail-open was written for the phase BEFORE a
 * session exists and quietly applied to every run after one did: a single flaky read — a phone that
 * drops its first request after backgrounding, a gateway blip — resolved to "nothing to pay",
 * nulled the session, and unmounted the sheet, discarding a half-entered card or billing's own
 * success screen. The whole point of failing open is that the tenant is never stranded; tearing down
 * a live payment is the opposite of that. Caught in review on #1936, after the give-up deadline was
 * fixed for the same reason — same defect, different trigger.
 *
 * ⚠️ ONE VENDOR. RES-1051 enforces a single party per booking, so this list is empty or holds one
 * entry. It is read as a list because that is the contract, and `.find` rather than `[0]` because a
 * second vendor should pick the payable one rather than whichever sorted first.
 */
export const useAddOnPaymentQuery = (
  buildingUuid: string | null,
  bookingId: number | null,
  isExpected: boolean,
  /*
   * RES-1336 — the widget reported a successful charge. It is the ONLY reliable signal that one was
   * made: the read below cannot tell "not paid yet" from "paid, not recorded yet", because both look
   * like `pending_payment`. Without it this would either stop reading too early and call a settled
   * charge an estimate, or poll on every unpaid order that was simply left alone.
   */
  hasCharged = false,
  /*
   * RES-1336 — the tenant has LEFT billing's sheet, however it ended. A second and WEAKER reason to
   * watch for a charge, and it exists because `hasCharged` can simply be lost: the widget fires it on
   * success and then renders its own screen, so anything that breaks in between — a throw after the
   * gateway already took the money — leaves a paid order this app never hears about. By then the
   * session has been found, so nothing re-reads: the poll above has stopped and no focus refetch is
   * armed. The confirmation goes on calling a real charge an estimate for as long as it is on screen.
   * Caught in review on #1936.
   *
   * ⛔ IT ARMS THE SETTLE WINDOW ONLY — NEVER THE FOCUS REFETCH — and that asymmetry is the whole of
   * it. `hasCharged` means billing reported a successful payment, which earns asking until the record
   * catches up. A dismissal only means a sheet was closed, and most are abandoned, declined or
   * expired; it earns one bounded 15s look. Arming focus from here would restore precisely the
   * every-focus-forever read that bounding focus to `hasCharged` was added to remove.
   *
   * ⚠️ It cannot bring the sheet back. `open` is the page's own state and the same dismissal set it
   * false, so a read that answers with a session finds the surface already closed.
   */
  hasDismissedPayment = false,
  /*
   * RES-1317 -- the money as it stood just before this order was submitted, so the read below can
   * tell "the server has not caught up" from "the server is finished". Null where nothing was
   * ordered from this session, which disarms it.
   */
  orderedPaymentKey: string | null = null,
) => {
  const apiUrl = useSelector((rootState: RootState) => rootState.config.apiUrl);
  const authToken = useSelector((rootState: RootState) => rootState.config.authToken);
  const isEnabled = Boolean(buildingUuid) && Boolean(bookingId) && isExpected;

  /*
   * The last answer that carried a session, so no later run can revoke it — see the note above.
   * Keyed by booking so a different order starts clean rather than inheriting its predecessor's.
   */
  const knownRef = useRef<{ bookingId: number | null; result: AddOnPaymentQueryResult } | null>(null);

  if (knownRef.current && knownRef.current.bookingId !== bookingId) {
    knownRef.current = null;
  }

  const known = (): AddOnPaymentQueryResult | null => knownRef.current?.result ?? null;

  /*
   * Every exit that would otherwise discard a session goes through here.
   *
   * The remembered answer is substituted whole except for `payment`, which is always the freshest
   * one. A session uuid and a settled charge do not go stale, but the payment block does: after an
   * amend it carries a disposition saying the order is being re-priced, and handing back the one
   * from before the amend would state the superseded charge as settled.
   */
  const withoutLosingSession = (result: AddOnPaymentQueryResult): AddOnPaymentQueryResult => {
    const remembered = result.checkoutSessionUuid ? null : known();

    return remembered ? { ...remembered, payment: result.payment } : result;
  };

  const remember = (result: AddOnPaymentQueryResult): AddOnPaymentQueryResult => {
    if (result.checkoutSessionUuid) {
      knownRef.current = { bookingId, result };
    }

    return result;
  };

  /*
   * ⛔ A REAL DEADLINE, started on the first enabled render. The previous `gcTime: PENDING_GIVE_UP_MS`
   * did NOT do this: gcTime evicts an INACTIVE query's cache and has no effect on an active poll, so
   * the documented "gives up at 45s" never existed and a stuck order polled forever behind a skeleton.
   * Lazily via a ref so the clock starts when the wait does, not when the component mounts.
   */
  const deadlineRef = useRef<number | null>(null);

  if (isEnabled && deadlineRef.current === null) {
    deadlineRef.current = Date.now() + PENDING_GIVE_UP_MS;
  }

  const isPastDeadline = (): boolean => deadlineRef.current !== null && Date.now() > deadlineRef.current;

  /*
   * This read still describes the order as it stood before the amend, so the money in it is not the
   * tenant's to see. Three bounds sit on it, and each closes a way the answer can be final already:
   * the deadline, because an unchanged fingerprint does NOT imply the server is still working; a
   * refusal, because work-orders wrote nothing and no change is coming; and a null baseline, because
   * nothing was ordered from this session to compare against.
   */
  const describesThePreviousOrder = (vendor: BookingAddOnVendorDto): boolean =>
    !isPastDeadline() &&
    !vendor.amendRefusedReason &&
    orderedPaymentKey !== null &&
    paymentIdentity(vendor.payment) === orderedPaymentKey;

  /* Either reason to think a charge may exist for this order — see both parameters above. */
  const isWatchingForCharge = hasCharged || hasDismissedPayment;

  /* Its own clock, started by whichever signal arrives first — see `SETTLE_GIVE_UP_MS`. */
  const settleDeadlineRef = useRef<number | null>(null);

  if (isWatchingForCharge && settleDeadlineRef.current === null) {
    settleDeadlineRef.current = Date.now() + SETTLE_GIVE_UP_MS;
  }

  const isPastSettleDeadline = (): boolean =>
    settleDeadlineRef.current !== null && Date.now() > settleDeadlineRef.current;

  return useQuery<AddOnPaymentQueryResult>({
    queryKey: ['add-on-payment', buildingUuid, bookingId],
    enabled: isEnabled,
    /*
     * Two reasons to keep asking, and they are consecutive rather than alternative: first until a
     * session exists, then — only once there is reason to think a charge was made — until the work
     * order records it. Both are bounded by their own deadline, and the second never starts on an
     * order whose sheet the tenant never even opened.
     */
    refetchInterval: query => {
      const result = query.state.data;

      if (result?.isAwaitingSession) {
        return PENDING_POLL_MS;
      }

      const isSettling = isWatchingForCharge && !result?.charge && !isPastSettleDeadline();

      return isSettling ? PENDING_POLL_MS : false;
    },
    /* ⛔ Never retry. A failure here means "no payment", and retrying would hold the wait open. */
    retry: false,
    /*
     * ⛔ ON FOCUS ONLY BETWEEN A REPORTED CHARGE AND ITS RECORD, which is the one window where coming
     * back to the tab can learn something this app has no other way to hear.
     *
     * It took two wrong shapes first. Flatly `false` meant a charge billing recorded AFTER the 15s
     * settle poll gave up was never picked up: the confirmation kept saying "estimated" until the
     * tenant left the screen and came back. Then `!charge` meant every focus fired two requests for
     * as long as the screen was mounted on any order that never produces a charge — dismissed,
     * declined, expired — which is most of them. `hasCharged` is the bound both were missing: it is
     * only true once the widget has reported a successful payment, so an abandoned order asks
     * nothing, and a recorded one stops asking the moment the charge lands.
     *
     * ⛔ `hasDismissedPayment` IS DELIBERATELY ABSENT FROM THIS CONDITION. It covers the same gap —
     * a charge made without the callback reaching us — but it is a guess where this one is a fact,
     * and it would be true on every abandoned order. It gets the bounded settle window above instead.
     *
     * ⚠️ It also keeps the sheet from reappearing. Pre-charge focus refetches could surface a session
     * that arrived after the give-up and re-open billing's overlay over a confirmation the tenant had
     * already reached, unannounced; with no pre-charge refetch there is nothing to surface it.
     *
     * ⚠️ And this is only safe at all because neither the deadline nor a failed read can revoke a
     * known session any more — see `withoutLosingSession`. While they could, this refetch was one of
     * the triggers that nulled a live session and tore the sheet down mid-payment.
     */
    refetchOnWindowFocus: query => hasCharged && !query.state.data?.charge,
    queryFn: async (): Promise<AddOnPaymentQueryResult> => {
      /*
       * ⛔ THE DEADLINE DECIDES ONE QUESTION ONLY: "will a session ever exist". It is applied here,
       * at the branches where none was found, and NOWHERE ELSE.
       *
       * It used to be a blanket early return at the top of this function, which answered a question
       * it had no business answering. `deadlineRef` starts when the order is placed and never
       * resets, so every run more than 45s later — the settle poll after a charge, a focus refetch,
       * a remount — returned `NOTHING_TO_PAY` and nulled a session that was perfectly real. That
       * unmounts the sheet: a half-entered card dropped, or billing's own success screen torn away
       * the moment it appeared, and the paid line never arriving. 45s is nothing next to a tenant
       * finding their wallet. Caught in review on #1936 by both reviewers independently.
       */
      const keepWaiting = (): AddOnPaymentQueryResult =>
        withoutLosingSession(isPastDeadline() ? NOTHING_TO_PAY : STILL_COMING);

      let vendors: Array<BookingAddOnVendorDto>;

      try {
        const client = new ResourceBookingServiceClient(apiUrl, authToken);

        /*
         * `.vendors` because the read answers the whole envelope now. This hook asks only whether a
         * checkout session has arrived for the order just placed, which the rows alone settle;
         * `canOrderAddOns` belongs to the ordered card, not to a create still in flight.
         */
        vendors = (await firstValueFrom(client.getBookingAddOns(buildingUuid as string, bookingId as number))).vendors;
      } catch {
        return withoutLosingSession(NOTHING_TO_PAY);
      }

      const created = vendors.find(vendor => vendor.status === ADD_ON_PAYMENT_STATUS.Created);

      /*
       * Polling alone does not help here: while it runs, this block is what the screen draws, so the
       * tenant reads a superseded "Total paid" over lines it does not cover. Withholding is the job;
       * the poll only decides how soon the right answer replaces it.
       *
       * Returned WITHOUT `withoutLosingSession`, which substitutes the remembered pre-amend answer
       * whenever no session is present -- exactly the figure being withheld.
       */
      if (created && describesThePreviousOrder(created)) {
        return STILL_COMING;
      }

      if (created) {
        const { payment } = created;

        if (payment?.checkoutSessionUuid) {
          return remember({
            checkoutSessionUuid: payment.checkoutSessionUuid,
            isAwaitingSession: false,
            /*
             * ⚠️ Read on EVERY run, not only while settling — it costs nothing, since the work
             * order is being read anyway, and `paymentState` is the system of record rather than
             * something this app infers. That covers a charge that landed without
             * `onPaymentComplete` reaching us: the widget throwing after the gateway took the
             * money, or the settle window simply running out before billing recorded it.
             *
             * ⛔ It does NOT rescue the 3-D Secure return, and an earlier revision of this note
             * claimed it did. That return is a fresh mount: `payableBookingId` is component state
             * and is gone, so this query never enables — and eligibility bounces the booking
             * anyway, since `!details?.add_ons?.length` is false once the order exists. That
             * tenant never reaches the confirmation at all, paid line or not.
             */
            charge: chargeFrom(payment),
            payment,
          });
        }

        /*
         * The request exists but carries no session yet — or work-orders could not be read for it,
         * which the service reports as a null payment rather than as "nothing to pay". Either way
         * billing may not have priced it yet, and `paymentState` cannot tell "not yet" from "never"
         * in the moments after creation, since automatic checkout sets it only once it runs. So keep
         * waiting and let the deadline decide.
         */
        return keepWaiting();
      }

      /* ⛔ The create never came back. The BOOKING STANDS — the add-ons are ordered, the charge is not. */
      if (vendors.some(vendor => vendor.status === ADD_ON_PAYMENT_STATUS.Failed)) {
        return withoutLosingSession(NOTHING_TO_PAY);
      }

      /* PENDING, or not written yet. Both are the middle of the hop. */
      return keepWaiting();
    },
  });
};
